import { Modal as AntModal } from 'antd'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import { restClient } from '@/Core'
import { cx } from '@/Ship/Helpers/cssClass'
import { offlineSyncManager } from '@/Ship/Pwa/OfflineSync'
import { pwaManager } from '@/Ship/Pwa/PwaManager'

import { CompleteTaskModalStore } from './CompleteTaskModalStore'

import styles from './Modals.module.css'

import type { RatingRow } from './Types'

interface CompleteTaskModalProps {
    open: boolean
    onClose: () => void
    assignmentUuid: string
    plannedLoadingDate?: string | null | undefined
    onCompleted: (uuid: string, newStatus: string) => void
}

/** Поп-ап «Завершення завдання»: оцінка кожного дня виконання (1–5) і коментар */
export const CompleteTaskModal = observer(
    ({ open, onClose, assignmentUuid, plannedLoadingDate, onCompleted }: CompleteTaskModalProps) => {
        const [store] = useState(() => new CompleteTaskModalStore())

        const today = dayjs().startOf('day')
        const planned = plannedLoadingDate ? dayjs(plannedLoadingDate).startOf('day') : today
        const startDate = planned.isBefore(today) ? planned : today

        const buildInitialRows = (): RatingRow[] => {
            const rows: RatingRow[] = []
            let cur = startDate
            while (!cur.isAfter(today)) {
                rows.push({ date: cur, rating: 5, comment: '' })
                cur = cur.add(1, 'day')
            }
            return rows.length ? rows : [{ date: today, rating: 5, comment: '' }]
        }

        useEffect(() => {
            if (open) {
                store.setRows(buildInitialRows())
                store.setDriverComment('')
            }
        }, [open])

        const handleSubmit = async () => {
            // Подвійне натискання не має ставити завершення в чергу двічі
            if (store.submitting) return
            // Поп-ап і є підтвердженням завершення: момент натискання «Так, підтвердити» — в аудит
            const confirmedAt = new Date().toISOString()

            // ── Offline: enqueue mutation, optimistic complete ────────────────
            if (pwaManager.isOffline) {
                store.setSubmitting(true)
                try {
                    await offlineSyncManager.enqueue({
                        type: 'complete-task',
                        uuid: assignmentUuid,
                        confirmedAt,
                        ratings: store.rows.map((r) => ({
                            ratingDate: r.date.format('YYYY-MM-DD'),
                            rating: r.rating,
                            comment: store.driverComment || null,
                        })),
                    })
                    onCompleted(assignmentUuid, 'completed_by_driver')
                    onClose()
                } catch {
                    store.setError('Не вдалось зберегти офлайн. Спробуйте ще раз.')
                } finally {
                    store.setSubmitting(false)
                }
                return
            }

            store.setSubmitting(true)
            store.setError(null)
            try {
                for (const row of store.rows) {
                    await restClient.post(`/services/executor-assignments/${assignmentUuid}/ratings`, {
                        json: {
                            ratingDate: row.date.format('YYYY-MM-DD'),
                            rating: row.rating,
                            comment: store.driverComment || null,
                        },
                    })
                }
                await restClient.post(`/services/executor-assignments/${assignmentUuid}/change-status`, {
                    json: { status: 'completed_by_driver', confirmedAt },
                })
                onCompleted(assignmentUuid, 'completed_by_driver')
                onClose()
            } catch {
                store.setError('Не вдалося завершити. Спробуйте ще раз.')
            } finally {
                store.setSubmitting(false)
            }
        }

        return (
            <AntModal
                open={open}
                title="Завершення завдання"
                onCancel={onClose}
                onOk={handleSubmit}
                okText="Так, підтвердити"
                cancelText="Скасувати"
                okButtonProps={{ loading: store.submitting, size: 'large' }}
                cancelButtonProps={{ size: 'large' }}
            >
                <div className={cx(styles, 'stack')}>
                    <p className={cx(styles, 'question')}>Ви дійсно бажаєте завершити завдання?</p>
                    <span className={cx(styles, 'hint')}>Оцініть кожен день виконання від 1 до 5</span>
                    {store.rows.map((row, i) => (
                        <div key={i} className={cx(styles, 'ratingRow')}>
                            <span className={cx(styles, 'ratingDate')}>{row.date.format('DD.MM.YYYY')}</span>
                            <div
                                className={cx(styles, 'stars')}
                                role="radiogroup"
                                aria-label={`Оцінка за ${row.date.format('DD.MM')}`}
                            >
                                {[1, 2, 3, 4, 5].map((v) => (
                                    <button
                                        key={v}
                                        type="button"
                                        role="radio"
                                        aria-checked={row.rating === v}
                                        aria-label={`${v}`}
                                        className={`${cx(styles, 'star')}${v <= row.rating ? ` ${cx(styles, 'star--on')}` : ''}`}
                                        onClick={() => store.updateRow(i, { rating: v })}
                                    >
                                        ★
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                    <label className={cx(styles, 'label')} htmlFor="complete-comment">
                        Коментар
                    </label>
                    <textarea
                        id="complete-comment"
                        className={`pwa-input ${cx(styles, 'textarea')}`}
                        rows={3}
                        placeholder="Необов'язково"
                        value={store.driverComment}
                        onChange={(e) => store.setDriverComment(e.target.value)}
                    />
                    {store.error && <div className="pwa-note pwa-note--danger">{store.error}</div>}
                </div>
            </AntModal>
        )
    }
)
